import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition } from '../hooks/useAnchoredPosition';
import { useIsMobile } from '../contexts/ViewportContext';

/**
 * A small floating panel anchored to whatever you wrap.
 *
 * The app had no popover at all — every explanatory hover was either a native
 * `title` (browser-styled, delayed, unselectable, and silently absent on touch)
 * or a one-off `absolute` block re-implemented per screen. This is the one
 * component for "extra detail on demand".
 *
 * ALWAYS PORTALLED. Call sites live inside scrolling table cells,
 * `overflow-hidden` cards and modals, where an `absolute` panel gets clipped —
 * the bug the inventory breakdown had before it moved here. Positioning comes
 * from `useAnchoredPosition`, so flipping and viewport clamping are the same
 * maths every other anchored panel already uses, rather than a third copy.
 *
 * TOUCH. Hover does not exist on a finger, so on mobile the trigger is
 * tap-only; `hover+click` collapses to `click` there rather than becoming
 * unreachable.
 *
 * NOT A MENU. `OverflowMenu` stays separate: arrow-key navigation, submenus and
 * `role="menu"` are a different contract that happens to also float.
 */

export interface AnchoredPanelProps {
    /** Element the panel is positioned against. */
    anchorRef: React.RefObject<HTMLElement | null>;
    open: boolean;
    /** Panel width in px. Default 240. */
    width?: number;
    align?: 'left' | 'right';
    /** Accessible name — becomes the panel's `aria-label`. */
    label?: string;
    id?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    children: React.ReactNode;
}

/**
 * The floating half of a popover: portalled to `document.body` and positioned
 * with `useAnchoredPosition`.
 *
 * Exported separately because some callers already own the open state and the
 * anchor — a stat card opens its panel on card hover and is itself clickable,
 * so wrapping it in {@link Popover}'s trigger button would nest a button inside
 * a button. They render this directly instead of hand-rolling a second portal.
 */
export const AnchoredPanel: React.FC<AnchoredPanelProps> = ({
    anchorRef,
    open,
    width = 240,
    align = 'left',
    label,
    id,
    onMouseEnter,
    onMouseLeave,
    children,
}) => {
    const pos = useAnchoredPosition(anchorRef, open, {
        estimatedWidth: width,
        estimatedHeight: 220,
        align,
        gap: 6,
    });
    if (!open || !pos) return null;
    return createPortal(
        <div
            id={id}
            role="group"
            aria-label={label}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width }}
            className="fixed z-[200] rounded-xl border border-border-subtle dark:border-border-subtle-dark bg-white dark:bg-surface-dark p-3 shadow-xl"
        >
            {children}
        </div>,
        document.body,
    );
};

export interface PopoverProps {
    /** Panel contents. */
    content: React.ReactNode;
    /**
     * `hover+click` (default) opens on pointer-over and pins on click;
     * `click` ignores hover, for content that should not appear in passing.
     * Mobile is tap-only either way.
     */
    trigger?: 'hover+click' | 'click';
    /** Accessible name for the trigger button. */
    label: string;
    /** Panel width hint for clamping. Default 240. */
    width?: number;
    align?: 'left' | 'right';
    className?: string;
    /** The anchor — the dots, a figure, a badge… */
    children: React.ReactNode;
}

/** Grace period so the pointer can travel from trigger to panel without it closing. */
const CLOSE_DELAY_MS = 180;

const Popover: React.FC<PopoverProps> = ({
    content,
    trigger = 'hover+click',
    label,
    width = 240,
    align = 'left',
    className = '',
    children,
}) => {
    const isMobile = useIsMobile();
    const [open, setOpen] = useState(false);
    /** Click pins the panel; hover alone does not, so it can close on leave. */
    const [pinned, setPinned] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const panelId = useId();

    const hoverEnabled = trigger === 'hover+click' && !isMobile;

    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);

    const closeNow = useCallback(() => {
        cancelClose();
        setOpen(false);
        setPinned(false);
    }, [cancelClose]);

    const scheduleClose = useCallback(() => {
        cancelClose();
        closeTimer.current = setTimeout(() => {
            // A pinned panel was opened deliberately — only an explicit dismiss
            // closes it.
            setOpen(prev => (pinned ? prev : false));
        }, CLOSE_DELAY_MS);
    }, [cancelClose, pinned]);

    useEffect(() => cancelClose, [cancelClose]);

    // Escape + outside pointer. Mirrors OverflowMenu so every floating panel
    // dismisses the same way.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeNow();
        };
        const onDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            if (triggerRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;
            closeNow();
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
        };
    }, [open, closeNow]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                aria-label={label}
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                onClick={e => {
                    // Mandatory: several call sites sit inside a row that is
                    // itself a button (PieceCard, the piece picker), where the
                    // click would otherwise also select the row.
                    e.stopPropagation();
                    if (open && pinned) closeNow();
                    else {
                        setOpen(true);
                        setPinned(true);
                    }
                }}
                onMouseEnter={hoverEnabled ? () => { cancelClose(); setOpen(true); } : undefined}
                onMouseLeave={hoverEnabled ? scheduleClose : undefined}
                onFocus={hoverEnabled ? () => { cancelClose(); setOpen(true); } : undefined}
                onBlur={hoverEnabled ? scheduleClose : undefined}
                className={`inline-flex items-center rounded-md focus:outline-none ${className}`}
            >
                {children}
            </button>
            <div ref={panelRef}>
                <AnchoredPanel
                    anchorRef={triggerRef}
                    open={open}
                    width={width}
                    align={align}
                    label={label}
                    id={panelId}
                    onMouseEnter={hoverEnabled ? cancelClose : undefined}
                    onMouseLeave={hoverEnabled ? scheduleClose : undefined}
                >
                    {content}
                </AnchoredPanel>
            </div>
        </>
    );
};

export default Popover;
